import {
  Absence,
  AccurateDateInterval,
  AccurateDateIntervalWithAbsence,
  AccurateDateIntervalWithShift,
  AccurateDateIntervalWithStay,
  AccurateTimeDetails,
  ActivityDeviationStatus,
  GroupedUserDataV2,
  PartialLocation,
  Shift,
  Stay,
  UnassignedShift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import {
  areIntervalsOverlapping,
  differenceInMinutes,
  isBefore,
} from 'date-fns';

interface PlannedLocationAndTimeDetails {
  date: string;
  location:
    | PartialLocation
    | {
        locationCode: string;
        name: string;
      };
  type: ActivityDeviationStatus;
  timeDifference: number;
}

type RowIndex = number;

export function determineAssignmentsOverlapsV2({
  shifts,
  absences,
  stays,
  isDebugEnabled,
}: {
  shifts: (
    | (Shift &
        WithPreparedAttributes & {
          id: string;
        })
    | UnassignedShift
  )[];
  absences: (Absence & { id: string })[];
  stays: Stay[];
  isDebugEnabled: boolean;
}): {
  shifts: AccurateDateIntervalWithShift[];
  absences: AccurateDateIntervalWithAbsence[];
  stays: AccurateDateIntervalWithStay[];
}[] {
  const items = {
    shifts: shifts.map((shift) => getShiftInterval(shift, isDebugEnabled)),
    absences: absences.map(getAbsenceInterval),
    stays: stays.map(getStaysInterval),
  };
  return constructRowsV2(items) as {
    shifts: AccurateDateIntervalWithShift[];
    absences: AccurateDateIntervalWithAbsence[];
    stays: AccurateDateIntervalWithStay[];
  }[];
}

export function constructRowsV2(
  items: Record<string, AccurateDateInterval[]>,
): Record<string, AccurateDateInterval[]>[] {
  const rows: Record<string, AccurateDateInterval[]>[] = [];
  const firstTwoRows: Record<string, AccurateDateInterval[]>[] = [];
  const lastRow: Record<string, AccurateDateInterval[]> = {
    shifts: [],
    absences: [],
    stays: [],
  };

  Object.entries(items).forEach(([outerKey, accurateDateIntervalItems]) => {
    accurateDateIntervalItems.forEach((accurateDateIntervalItem) => {
      let isItemPlacedInFirstTwoRow = false;

      for (let rowIndex = 0; rowIndex < 2; rowIndex++) {
        if (!firstTwoRows[rowIndex]) {
          firstTwoRows[rowIndex] = {};
          Object.keys(items).forEach((innerKey) => {
            firstTwoRows[rowIndex][innerKey] = [];
          });
        }

        const canPlace = !Object.keys(firstTwoRows[rowIndex]).some((innerKey) =>
          firstTwoRows[rowIndex][innerKey].some(
            (existingAccurateDateIntervalItem) =>
              isOverlapDetected(
                existingAccurateDateIntervalItem,
                accurateDateIntervalItem,
              ),
          ),
        );

        if (canPlace) {
          firstTwoRows[rowIndex][outerKey].push(accurateDateIntervalItem);
          isItemPlacedInFirstTwoRow = true;
          break;
        }
      }

      if (!isItemPlacedInFirstTwoRow) {
        lastRow[outerKey].push(accurateDateIntervalItem);
      }
    });
  });

  rows.push(...firstTwoRows);

  if (Object.values(lastRow).some((items) => items.length > 0)) {
    rows.push(lastRow);
  }
  return rows;
}
export function determineClusterFromClickPosition(
  clickPositionDatetime: Date,
  lastUserRow: GroupedUserDataV2,
): {
  shifts: AccurateDateIntervalWithShift[];
  absences: AccurateDateIntervalWithAbsence[];
  stays: AccurateDateIntervalWithStay[];
} {
  const cluster: {
    shifts: AccurateDateIntervalWithShift[];
    absences: AccurateDateIntervalWithAbsence[];
    stays: AccurateDateIntervalWithStay[];
  } = {
    shifts: [],
    absences: [],
    stays: [],
  };

  Object.entries(lastUserRow).forEach(([key, value]) => {
    value.forEach(
      (
        item:
          | AccurateDateIntervalWithShift
          | AccurateDateIntervalWithAbsence
          | AccurateDateIntervalWithStay,
      ) => {
        const isOverlapping =
          item.accurateStartDateTime <= clickPositionDatetime &&
          clickPositionDatetime <= item.accurateEndDateTime;

        if (isOverlapping) {
          if (key === 'shifts') {
            cluster.shifts.push(item as AccurateDateIntervalWithShift);
          } else if (key === 'absences') {
            cluster.absences.push(item as AccurateDateIntervalWithAbsence);
          } else if (key === 'stays') {
            cluster.stays.push(item as AccurateDateIntervalWithStay);
          }
        }
      },
    );
  });

  return cluster;
}

export function determineUnassignedShiftsOverlaps({
  shifts,
  isDebugEnabled,
}: {
  shifts: UnassignedShift[];
  isDebugEnabled: boolean;
}): {
  maxRowIndex: RowIndex;
  shiftRowIndexMap: {
    [shiftId: string]: RowIndex;
  };
  rows: Record<
    'shifts',
    Array<
      AccurateDateInterval & {
        shift: UnassignedShift;
      }
    >
  >[];
} {
  let maxRowIndex = 0;
  const outerKey = 'shifts';
  const rows = [] as Record<
    string,
    Array<
      AccurateDateInterval & {
        shift: UnassignedShift;
      }
    >
  >[];
  const rowIndexMap: { [shiftId: string]: RowIndex } = {};
  shifts.forEach((shift, index) => {
    const shiftInterval = getShiftInterval(shift, isDebugEnabled);
    const newRow: Record<
      string,
      Array<
        AccurateDateInterval & {
          shift: UnassignedShift;
        }
      >
    > = {};
    newRow[outerKey] = [shiftInterval];

    if (index === 0) {
      rows.push(newRow);
      rowIndexMap[shift.id] = index;
    } else {
      const rowIndex = getItemRowIndex(rows, shiftInterval, isDebugEnabled);
      maxRowIndex = rowIndex > maxRowIndex ? rowIndex : maxRowIndex;
      if (rows[rowIndex]) {
        rows[rowIndex][outerKey].push(shiftInterval);
        rowIndexMap[shift.id] = rowIndex;
      } else {
        rows[rowIndex] = newRow;
        rowIndexMap[shift.id] = rowIndex;
      }
    }
  });

  return {
    maxRowIndex,
    shiftRowIndexMap: rowIndexMap,
    rows,
  };
}

export function determineAssignmentsOverlaps({
  shifts,
  absences,
  stays,
  isDebugEnabled,
}: {
  shifts: (
    | (Shift &
        WithPreparedAttributes & {
          id: string;
        })
    | UnassignedShift
  )[];
  absences: (Absence & { id: string })[];
  stays: Stay[];
  isDebugEnabled: boolean;
}): {
  shifts: AccurateDateIntervalWithShift[];
  absences: AccurateDateIntervalWithAbsence[];
  stays: AccurateDateIntervalWithStay[];
}[] {
  const items = {
    shifts: shifts.map((shift) => getShiftInterval(shift, isDebugEnabled)),
    absences: absences.map(getAbsenceInterval),
    stays: stays.map(getStaysInterval),
  };
  return constructRows(items, isDebugEnabled) as {
    shifts: AccurateDateIntervalWithShift[];
    absences: AccurateDateIntervalWithAbsence[];
    stays: AccurateDateIntervalWithStay[];
  }[];
}

export function constructRows(
  items: Record<string, AccurateDateInterval[]>,
  isDebugEnabled: boolean,
): Record<string, AccurateDateInterval[]>[] {
  const rows = [] as Record<string, AccurateDateInterval[]>[];
  Object.entries(items).forEach(([outerKey, accurateDateIntervalItems]) => {
    accurateDateIntervalItems.forEach((accurateDateIntervalItem) => {
      const newRow: Record<string, AccurateDateInterval[]> = {};
      Object.keys(items).forEach((innerKey) => {
        newRow[innerKey] =
          outerKey === innerKey ? [accurateDateIntervalItem] : [];
      });

      if (rows.length === 0) {
        rows.push(newRow);
      } else {
        const rowIndex = getItemRowIndex(
          rows,
          accurateDateIntervalItem,
          isDebugEnabled,
        );
        if (rows[rowIndex]) {
          rows[rowIndex][outerKey].push(accurateDateIntervalItem);
        } else {
          rows[rowIndex] = newRow;
        }
      }
    });
  });

  return rows;
}

export function getStaysInterval<T extends Stay>(
  stay: T,
): AccurateDateInterval & { stay: T } {
  return {
    accurateStartDateTime: createDateTimeWithoutSeconds(stay.startDatetime),
    accurateEndDateTime: createDateTimeWithoutSeconds(stay.endDatetime),
    stay,
  };
}

export function getAbsenceInterval<T extends Absence & { id: string }>(
  absence: T,
): AccurateDateInterval & { absence: T } {
  return {
    accurateStartDateTime: createDateTimeWithoutSeconds(absence.absentFrom),
    accurateEndDateTime: createDateTimeWithoutSeconds(absence.absentTo),
    absence,
  };
}

export function createDateTimeWithoutSeconds(time: string): Date {
  const date = new Date(time);
  // UTC setter: the local `setSeconds` re-resolves the wall-clock time, which shifts
  // times in the repeated DST hour (e.g. 02:00 CET -> 02:00 CEST) one hour back
  date.setUTCSeconds(0, 0);
  return date;
}

export function getShiftInterval<
  T extends
    | (Shift &
        WithPreparedAttributes & {
          id: string;
        })
    | UnassignedShift,
>(shift: T, isDebugEnabled: boolean): AccurateDateInterval & { shift: T } {
  const { startDatetime, endDatetime } = determineShiftRenderDatetime(
    shift,
    isDebugEnabled,
  );

  return {
    accurateStartDateTime: createDateTimeWithoutSeconds(startDatetime.date),
    accurateEndDateTime: createDateTimeWithoutSeconds(endDatetime.date),
    shift,
  };
}

export function determineShiftRenderDatetime(
  shift:
    | (Shift &
        WithPreparedAttributes & {
          id: string;
        })
    | UnassignedShift,
  isDebugEnabled: boolean,
): {
  startDatetime: AccurateTimeDetails;
  endDatetime: AccurateTimeDetails;
} {
  const startDatetime = getStartDatetime(shift);
  const endDatetime = getEndDatetime(shift);

  if (isBefore(new Date(startDatetime.date), new Date(endDatetime.date))) {
    return {
      startDatetime,
      endDatetime,
    };
  } else {
    if (isDebugEnabled) {
      console.info(
        `Bad time interval, start datetime: ${startDatetime.date} is behind end datetime: ${endDatetime.date}. Using planned time instead.`,
      );
    }
    return {
      startDatetime: getPlannedStartDatetime(shift),
      endDatetime: getPlannedEndDatetime(shift),
    };
  }
}

export function getItemRowIndex(
  rows: Record<string, AccurateDateInterval[]>[],
  item: AccurateDateInterval,
  isDebugEnabled: boolean,
): number {
  let itemRowIndex = 0;

  rows.forEach((row, index) => {
    if (itemRowIndex === index) {
      try {
        const isOverlappingWithExistingItemsInRow = Object.keys(row).some(
          (itemKey) => {
            const itemsOfThisTypeInRow = row[itemKey];

            return isOverlappingWithOneItem(itemsOfThisTypeInRow, item);
          },
        );

        if (isOverlappingWithExistingItemsInRow) {
          itemRowIndex = index + 1;
        } else {
          itemRowIndex = index;
        }
      } catch (e) {
        if (isDebugEnabled) {
          console.warn(e);
          console.warn(item);
        }
      }
    }
  });

  return itemRowIndex;
}

export function getStartDatetime({
  startDatetime,
  deviatedStartDatetime,
  reportedStartDatetime,
  reportedStartLocation,
  startLocation,
}: Pick<
  WithPreparedAttributes | UnassignedShift,
  | 'startDatetime'
  | 'deviatedStartDatetime'
  | 'reportedStartDatetime'
  | 'reportedStartLocation'
  | 'startLocation'
>): AccurateTimeDetails {
  if (reportedStartDatetime) {
    const timeDifference = differenceInMinutes(
      new Date(reportedStartDatetime),
      new Date(startDatetime),
    );
    return {
      date: reportedStartDatetime,
      location: reportedStartLocation || startLocation,
      type: ActivityDeviationStatus.Reported,
      timeDifference: timeDifference,
    };
  } else if (deviatedStartDatetime) {
    const timeDifference = differenceInMinutes(
      new Date(deviatedStartDatetime),
      new Date(startDatetime),
    );
    return {
      date: deviatedStartDatetime,
      location: startLocation,
      type: ActivityDeviationStatus.Deviated,
      timeDifference: timeDifference,
    };
  } else {
    return getPlannedStartDatetime({
      startDatetime,
      startLocation,
    });
  }
}

export function getPlannedStartDatetime({
  startDatetime,
  startLocation,
}: Pick<
  WithPreparedAttributes | UnassignedShift,
  'startDatetime' | 'startLocation'
>): PlannedLocationAndTimeDetails {
  return {
    date: startDatetime,
    location: startLocation,
    type: ActivityDeviationStatus.Planned,
    timeDifference: 0,
  };
}

export function isOverlappingWithOneItem(
  items: AccurateDateInterval[],
  itemToCompareWith: AccurateDateInterval,
): boolean {
  return (
    items.findIndex((item) => isOverlapDetected(item, itemToCompareWith)) > -1
  );
}

export function isOverlapDetected(
  itemInterval: AccurateDateInterval,
  determinedItemInterval: AccurateDateInterval,
): boolean {
  // areIntervalsOverlapping throws on invalid intervals, which would abort the row
  // calculation for every user in the request and leave the timeline empty
  if (
    !isValidInterval(itemInterval) ||
    !isValidInterval(determinedItemInterval)
  ) {
    return false;
  }

  return areIntervalsOverlapping(
    {
      start: itemInterval.accurateStartDateTime,
      end: itemInterval.accurateEndDateTime,
    },
    {
      start: determinedItemInterval.accurateStartDateTime,
      end: determinedItemInterval.accurateEndDateTime,
    },
  );
}

function isValidInterval(interval: AccurateDateInterval): boolean {
  // NaN (invalid date) comparisons are false, so this also rejects invalid dates
  return (
    interval.accurateStartDateTime.getTime() <=
    interval.accurateEndDateTime.getTime()
  );
}

export function getPlannedEndDatetime({
  endDatetime,
  endLocation,
}: Pick<
  WithPreparedAttributes | UnassignedShift,
  'endDatetime' | 'endLocation'
>): PlannedLocationAndTimeDetails {
  return {
    date: endDatetime,
    location: endLocation,
    type: ActivityDeviationStatus.Planned,
    timeDifference: 0,
  };
}

export function getEndDatetime({
  endDatetime,
  deviatedEndDatetime,
  reportedEndDatetime,
  reportedEndLocation,
  endLocation,
}: Pick<
  WithPreparedAttributes | UnassignedShift,
  | 'endDatetime'
  | 'deviatedEndDatetime'
  | 'reportedEndDatetime'
  | 'reportedEndLocation'
  | 'endLocation'
>): AccurateTimeDetails {
  if (reportedEndDatetime) {
    const timeDifference = differenceInMinutes(
      new Date(reportedEndDatetime),
      new Date(endDatetime),
    );
    return {
      date: reportedEndDatetime,
      location: reportedEndLocation || endLocation,
      type: ActivityDeviationStatus.Reported,
      timeDifference: timeDifference,
    };
  } else if (deviatedEndDatetime) {
    const timeDifference = differenceInMinutes(
      new Date(deviatedEndDatetime),
      new Date(endDatetime),
    );
    return {
      date: deviatedEndDatetime,
      location: endLocation,
      type: ActivityDeviationStatus.Deviated,
      timeDifference: timeDifference,
    };
  } else {
    return getPlannedEndDatetime({
      endDatetime,
      endLocation,
    });
  }
}
